/**
 * The hosts a SmartLink page can be advertised on.
 *
 * They all resolve the same account subdomains in production, so the choice
 * only decides which host is canonical: the address we build into a share url,
 * an embed url, an OG tag and the address field in the app. An account picks a
 * default and a single SmartLink may override it.
 *
 * This is not `AccountModel.customDomain`, which is a domain the artist owns.
 */
export const smartLinkPlatformDomains = ['music24.link', 'fym.fm', 'music.ht', 'music.mq'] as const;

export type SmartLinkPlatformDomain = (typeof smartLinkPlatformDomains)[number];

/** What an account, and therefore a SmartLink, gets when it picks nothing. */
export const defaultSmartLinkPlatformDomain: SmartLinkPlatformDomain = 'music24.link';

/** Whether a value is one of the domains we serve. */
export function isSmartLinkPlatformDomain(value: unknown): value is SmartLinkPlatformDomain {
  return typeof value === 'string' && (smartLinkPlatformDomains as readonly string[]).includes(value);
}

/**
 * The domain a SmartLink is advertised on: its own choice, else its account's,
 * else the default.
 */
export function resolveSmartLinkPlatformDomain(
  smartLinkDomain: string | null | undefined,
  accountDomain: string | null | undefined
): SmartLinkPlatformDomain {
  if (isSmartLinkPlatformDomain(smartLinkDomain)) {
    return smartLinkDomain;
  }
  if (isSmartLinkPlatformDomain(accountDomain)) {
    return accountDomain;
  }
  return defaultSmartLinkPlatformDomain;
}
